import React, { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { HOME, PROFILE, ACCOUNT_DELETION } from '../routes/paths';
import { getLoginDestination } from '../utils/authFlow';
import { getAuthToken, notifyAuthChanged, subscribeToAuth } from '../utils/authSession';
import './Header.css';

const Header = () => {
  const token = useSyncExternalStore(subscribeToAuth, getAuthToken, () => null);
  const location = useLocation();
  const isDeletionPage = location.pathname.replace(/\/$/, '') === ACCOUNT_DELETION.replace(/\/$/, '');
  const brand = isDeletionPage ? 'Click Events' : 'Click AI';
  const navigate = useNavigate();
  const [accountOpen, setAccountOpen] = useState(false);
  const accountRef = useRef(null);
  const triggerRef = useRef(null);

  useEffect(() => { setAccountOpen(false); }, [location.pathname, token]);

  useEffect(() => {
    if (!accountOpen) return;
    function dismiss(event) {
      if (!accountRef.current?.contains(event.target)) setAccountOpen(false);
    }
    function escape(event) {
      if (event.key === 'Escape') {
        setAccountOpen(false);
        triggerRef.current?.focus();
      }
    }
    document.addEventListener('pointerdown', dismiss);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('pointerdown', dismiss);
      document.removeEventListener('keydown', escape);
    };
  }, [accountOpen]);

  function logout() {
    setAccountOpen(false);
    ['authToken', 'isCompleteAccount', 'tempMobileNumber', 'eventCode', 'pendingInvitationToken', 'pendingInvitationResponse'].forEach((key) => localStorage.removeItem(key));
    sessionStorage.removeItem("pendingAuthToken");
    sessionStorage.removeItem("pendingMobileNumber");
    notifyAuthChanged();
    navigate(HOME, { replace: true });
  }

  return (
    <header className="app-header">
      <div className="header-content">
        <Link to={HOME} className="logo-container" aria-label={`${brand} home`}>
          <img src="/logo.png" alt={`${brand} Logo`} className="app-logo" />
          <span className="app-name">{brand}</span>
        </Link>
        <nav className="header-nav" aria-label="Main navigation">
          {location.pathname === HOME && <a href="#how-it-works" className="header-how">How it works</a>}
          {token ? <div className="header-account" ref={accountRef} onBlur={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget)) setAccountOpen(false);
          }}>
            <button type="button" ref={triggerRef} className="header-profile" aria-label="Account options" title="Account options"
              aria-expanded={accountOpen} aria-controls="header-account-options" onClick={() => setAccountOpen((open) => !open)}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true" focusable="false">
                <circle cx="12" cy="8" r="4" />
                <path d="M4 21v-2a8 8 0 0 1 16 0v2" />
              </svg>
            </button>
            {accountOpen && <div id="header-account-options" className="header-account-options">
              <p className="header-account-caption">MY ACCOUNT</p>
              <Link to={PROFILE} aria-label="Profile" aria-current={location.pathname === PROFILE ? 'page' : undefined} onClick={() => setAccountOpen(false)}>
                <span className="header-option-icon" aria-hidden="true">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
                    <circle cx="12" cy="8" r="3.5" /><path d="M5 20v-1a7 7 0 0 1 14 0v1" />
                  </svg>
                </span>
                <span className="header-option-copy"><span>Profile</span><small>View your account</small></span>
                <span className="header-option-arrow" aria-hidden="true">›</span>
              </Link>
              <div className="header-account-divider" />
              <button className="header-logout" type="button" aria-label="Logout" onClick={logout}>
                <span className="header-option-icon" aria-hidden="true">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M10 4H5v16h5M10 12h11m-4-4 4 4-4 4" />
                  </svg>
                </span>
                <span className="header-option-copy"><span>Logout</span><small>Sign out of Click AI</small></span>
              </button>
            </div>}
          </div> : <Link className="header-login" to={getLoginDestination(location.pathname, location.search)}>Login / Signup</Link>}
        </nav>
      </div>
    </header>
  );
};

export default Header;
